import React, { useState, useEffect, useContext, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { AuthContext } from "../../../context/AuthContext";
import { useDbContext } from "../../../context/DbContext";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import SherComboBox from "../../ComboBoxes/SherComboBox";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import { selectActiveFyid, selectActiveFy } from "../../../app/financialYearSlice";
import { selectActiveLocation } from "../../../app/locationSlice";
import { useActiveFyLimits } from "../../../utils/fyDateValidation";

function ReceiptList({ onClose, openReceiptListReport, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const { agentCode } = useContext(AuthContext);
  const activeFyid = useSelector(selectActiveFyid);
  const activeFy = useSelector(selectActiveFy);
  const fyid = activeFyid || Number(sessionStorage.getItem("selectedFyId")) || Number(localStorage.getItem("selectedFyId")) || 0;
  const activeLocation = useSelector(selectActiveLocation);

  const storedLocId = sessionStorage.getItem("loginLocationId") || localStorage.getItem("loginLocationId");
  const storedLocName = sessionStorage.getItem("loginLocation") || localStorage.getItem("loginLocation");

  const [userType, setuserType] = useState("");

  useEffect(() => {
    const storeduType = sessionStorage.getItem("userType");
    if (storeduType) {
      setuserType(storeduType);
    }
  }, []);

  const { minDate, maxDate, isDateInFy } = useActiveFyLimits();

  const getCurrentDate = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const today = getCurrentDate();

  // ── Form State ────────────────────────────────────────────────────────────
  const [receiptListReportData, setReceiptListReportData] = useState({
    fromDate:   today,
    toDate:     today,
    reportType: { value: "Simple", label: "Simple" },
    customer:   { value: "", label: "" },
    agent:      { value: "", label: "" },
    accno:      { value: "", label: "" },
    mobileNo:   "",
    location: activeLocation?.id
      ? { value: String(activeLocation.id), label: activeLocation.name }
      : (storedLocId ? { value: String(storedLocId), label: storedLocName || "SHOP" } : { value: "0", label: "All Locations" }),
  });

  const set = (key, val) => setReceiptListReportData((prev) => ({ ...prev, [key]: val }));

  const hasAlertedFyRef = useRef(false);
  useEffect(() => {
    if (activeFy && !hasAlertedFyRef.current) {
      if (!isDateInFy(today)) {
        alert("Current date is outside the active financial year");
        hasAlertedFyRef.current = true;
      }
    }
  }, [activeFy, today, isDateInFy]);

  // ── Data Options ──────────────────────────────────────────────────────────
  const [RegcnameData, setRegCnameData] = useState([]);
  const [agentName,    setagentName]    = useState([]);
  const [accNo,        setaccNo]        = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Customers
        const custRes = await axios.get(`${apiBaseUrl}/main/SchRec_schemeregisteredcustomers/${dbCode}`);
        setRegCnameData(
          (custRes.data || []).map((item) => ({
            value: item.accname,
            label: item.LedName,
          }))
        );

        // Fetch Agents
        const agentRes = await axios.get(`${apiBaseUrl}/main/schsalesmanNames/${dbCode}`);
        setagentName(
          (agentRes.data || []).map((item) => ({
            value: item.Auto ?? item.auto,
            label: item.Name || item.name,
          }))
        );

        // Fetch Accounts
        const accRes = await axios.get(`${apiBaseUrl}/main/SchRec_Accno/${dbCode}`);
        const seenAcc = new Set();
        const accOpts = [];
        (accRes.data || []).forEach((item) => {
          const numStr = String(item.accno ?? "").trim();
          if (numStr && !seenAcc.has(numStr)) {
            seenAcc.add(numStr);
            accOpts.push({ value: numStr, label: numStr });
          }
        });
        setaccNo(accOpts);

        // Fetch Locations
        const locRes = await axios.get(`${apiBaseUrl}/otherregistration/getoptions/${dbCode}`);
        const locations = (locRes.data || []).filter(
          (rec) => String(rec.Type || rec.type || "").toLowerCase() === "location"
        );
        const mappedLocs = locations.map((rec) => ({
          value: String(rec.Auto ?? rec.auto ?? rec.SlNo ?? rec.slno ?? ""),
          label: rec.Name || rec.name || "",
        }));
        const opts = [{ value: "0", label: "All Locations" }, ...mappedLocs];
        setLocationOptions(opts);

        if (opts.length > 0) {
          const targetId = activeLocation?.id || storedLocId;
          const match = targetId ? opts.find((opt) => Number(opt.value) === Number(targetId)) : null;
          set("location", match || opts[0]);
        }
      } catch (error) {
        console.error("Error fetching ReceiptList data:", error.message);
      }
    };
    if (dbCode) fetchData();
  }, [apiBaseUrl, dbCode, activeLocation]);

  const reportTypeOptions = [
    { value: "Simple",    label: "Simple" },
    { value: "Date wise", label: "Date wise" },
  ];

  return (
    <div
      className="absolute inset-0 w-full h-full bg-black/40 flex items-center justify-center pointer-events-auto"
      style={{ zIndex }}
    >
      <div className="bg-white w-[640px] shadow-[0_8px_30px_rgb(0,0,0,0.12)] flex flex-col absolute overflow-hidden rounded-xl border border-slate-200">
        
        {/* ── Header ── */}
        <WindowHeader
          title="Receipt List"
          logo={headeLogo}
          onClose={onClose}
          style={{ height: "36px" }}
          className="bg-[#0f56d3] text-white"
          titleClassName="text-xs font-semibold tracking-wide"
          closeBtnClassName="w-6 h-6 rounded-[5px] border-none mr-2 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white transition-colors"
        />

        {/* ── Body ── */}
        <div className="p-4 flex flex-col gap-3 bg-slate-50/50">
          
          {/* Period Row */}
          <div className="grid grid-cols-[130px_1fr] items-center gap-[10px]">
            <label className="text-[11px] font-bold text-slate-700 font-['Inter']">Period</label>
            <div className="grid grid-cols-2 gap-[10px]">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-slate-400 font-semibold font-['Inter'] whitespace-nowrap">From</span>
                <input
                  type="date"
                  min={minDate}
                  max={maxDate}
                  value={receiptListReportData.fromDate}
                  onChange={(e) => set("fromDate", e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val && !isDateInFy(val)) {
                      alert("Date incompatible with financial year");
                    }
                  }}
                  className="w-full h-[28px] px-2 border border-slate-300 rounded text-[11px] font-semibold text-slate-800 font-['Inter'] outline-none bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20 transition-all"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-slate-400 font-semibold font-['Inter'] whitespace-nowrap">To</span>
                <input
                  type="date"
                  min={minDate}
                  max={maxDate}
                  value={receiptListReportData.toDate}
                  onChange={(e) => set("toDate", e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val && !isDateInFy(val)) {
                      alert("Date incompatible with financial year");
                    }
                  }}
                  className="w-full h-[28px] px-2 border border-slate-300 rounded text-[11px] font-semibold text-slate-800 font-['Inter'] outline-none bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20 transition-all"
                />
              </div>
            </div>
          </div>

          <div className="w-full h-px bg-slate-200 my-1" />

          {/* Report Type */}
          <div className="grid grid-cols-[130px_1fr] items-center gap-[10px] w-full">
            <label className="text-[11px] font-bold text-slate-700 font-['Inter'] text-left whitespace-nowrap">
              Report Type
            </label>
            <div className="w-full">
              <SherComboBox
                options={reportTypeOptions}
                value={receiptListReportData.reportType?.label || ""}
                onSelect={(opt) => set("reportType", opt)}
                onInputChange={(text) => set("reportType", { value: text, label: text })}
                onFreeInput={(text) => set("reportType", { value: text, label: text })}
                placeholder="Select Type..."
                inputClassName="text-[11px] font-['Inter'] font-semibold text-slate-900 border border-slate-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20"
                inputHeight="28px"
                inputWidth="100%"
              />
            </div>
          </div>

          {/* Scheme Customer */}
          <div className="grid grid-cols-[130px_1fr] items-center gap-[10px] w-full">
            <label className="text-[11px] font-bold text-slate-700 font-['Inter'] text-left whitespace-nowrap">
              Scheme Customer
            </label>
            <div className="w-full">
              <SherComboBox
                options={RegcnameData}
                value={receiptListReportData.customer?.label || ""}
                onSelect={(opt) => set("customer", opt)}
                onInputChange={(text) => set("customer", { value: text, label: text })}
                onFreeInput={(text) => set("customer", { value: text, label: text })}
                placeholder="Select Customer..."
                inputClassName="text-[11px] font-['Inter'] font-semibold text-slate-900 border border-slate-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20"
                inputHeight="28px"
                inputWidth="100%"
              />
            </div>
          </div>

          {/* Scheme Agent */}
          <div className="grid grid-cols-[130px_1fr] items-center gap-[10px] w-full">
            <label className="text-[11px] font-bold text-slate-700 font-['Inter'] text-left whitespace-nowrap">
              Scheme Agent
            </label>
            <div className="w-full">
              <SherComboBox
                options={agentName}
                value={receiptListReportData.agent?.label || ""}
                onSelect={(opt) => set("agent", opt)}
                onInputChange={(text) => set("agent", { value: text, label: text })}
                onFreeInput={(text) => set("agent", { value: text, label: text })}
                placeholder="Select Agent..."
                inputClassName="text-[11px] font-['Inter'] font-semibold text-slate-900 border border-slate-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20"
                inputHeight="28px"
                inputWidth="100%"
              />
            </div>
          </div>

          {/* Account Number */}
          <div className="grid grid-cols-[130px_1fr] items-center gap-[10px] w-full">
            <label className="text-[11px] font-bold text-slate-700 font-['Inter'] text-left whitespace-nowrap">
              Account Number
            </label>
            <div className="w-full">
              <SherComboBox
                options={accNo}
                value={receiptListReportData.accno?.label || ""}
                onSelect={(opt) => set("accno", opt || { value: "", label: "" })}
                onInputChange={(text) => set("accno", { value: text, label: text })}
                onFreeInput={(text) => set("accno", { value: text, label: text })}
                onClear={() => set("accno", { value: "", label: "" })}
                placeholder="Select Account..."
                inputClassName="text-[11px] font-['Inter'] font-semibold text-slate-900 border border-slate-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20"
                inputHeight="28px"
                inputWidth="100%"
              />
            </div>
          </div>

          {/* Location */}
          <div className="grid grid-cols-[130px_1fr] items-center gap-[10px] w-full">
            <label className="text-[11px] font-bold text-slate-700 font-['Inter'] text-left whitespace-nowrap">
              Location
            </label>
            <div className="w-full">
              <SherComboBox
                options={locationOptions}
                value={receiptListReportData.location?.label || ""}
                onSelect={(opt) => set("location", opt)}
                onInputChange={(text) => set("location", { value: text, label: text })}
                onFreeInput={(text) => set("location", { value: text, label: text })}
                placeholder="Select Location..."
                inputClassName="text-[11px] font-['Inter'] font-semibold text-slate-900 border border-slate-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20"
                inputHeight="28px"
                inputWidth="100%"
              />
            </div>
          </div>

          {/* Mobile Number */}
          <div className="grid grid-cols-[130px_1fr] items-center gap-[10px] w-full">
            <label className="text-[11px] font-bold text-slate-700 font-['Inter'] text-left whitespace-nowrap">
              Mobile Number
            </label>
            <div className="w-full">
              <input
                type="text"
                value={receiptListReportData.mobileNo}
                onChange={(e) => set("mobileNo", e.target.value)}
                placeholder="Enter Mobile No..."
                className="w-full h-[28px] px-2 border border-slate-300 rounded text-[11px] font-semibold text-slate-800 font-['Inter'] outline-none bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20 transition-all"
              />
            </div>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="flex justify-end items-center gap-2 border-t border-slate-200 bg-slate-50 px-4 py-2.5">
          <button
            onClick={() => {
              if (receiptListReportData.fromDate && !isDateInFy(receiptListReportData.fromDate)) {
                alert("From date incompatible with financial year");
                return;
              }
              if (receiptListReportData.toDate && !isDateInFy(receiptListReportData.toDate)) {
                alert("To date incompatible with financial year");
                return;
              }

              // Resolve customer ID from label if value is not already a valid code
              let resolvedCustomer = receiptListReportData.customer;
              if (resolvedCustomer && resolvedCustomer.label) {
                const found = RegcnameData.find(item => item.label === resolvedCustomer.label);
                if (found) resolvedCustomer = found;
              }

              // Resolve agent ID from label
              let resolvedAgent = receiptListReportData.agent;
              if (resolvedAgent && resolvedAgent.label) {
                const found = agentName.find(item => item.label === resolvedAgent.label);
                if (found) resolvedAgent = found;
              }

              // Resolve account number
              let resolvedAcc = receiptListReportData.accno;
              if (resolvedAcc && resolvedAcc.label) {
                const found = accNo.find(item => item.label === resolvedAcc.label);
                if (found) resolvedAcc = found;
                else resolvedAcc = { value: resolvedAcc.label, label: resolvedAcc.label };
              }

              const resolvedLoc = receiptListReportData.location?.value !== undefined && receiptListReportData.location?.value !== ""
                ? Number(receiptListReportData.location.value)
                : 0;

              openReceiptListReport({
                ...receiptListReportData,
                customer: resolvedCustomer,
                agent: resolvedAgent,
                accno: resolvedAcc,
                fyid,
                locationId: isNaN(resolvedLoc) ? 0 : resolvedLoc,
              });
            }}
            className="flex items-center gap-1.5 px-4 h-[30px] rounded-md border border-slate-300 bg-white hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer shadow-sm text-[11px] font-bold text-[#0f56d3] font-['Inter']"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Show Report
          </button>
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 px-4 h-[30px] rounded-md border border-slate-300 bg-white hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer shadow-sm text-[11px] font-bold text-slate-600 font-['Inter']"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#D32F2F" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            Exit
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReceiptList;
